import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';

// Client contacts set up to approve contractor timesheets, with their portal
// access. It was the Approvers view of the External Resources page and is now
// the Approvers view of the Contractor Dashboard. An approver is made by
// setting a contract's Timesheet Approver to a client contact, so there is no
// add button here: the list is everyone who approves at least one contract.
// A contractor's name under an approver opens that contractor's record.
export default function ApproversPanel({ onOpenContractor }) {
  const [approvers, setApprovers] = useState([]);
  const [approversLoading, setApproversLoading] = useState(false);
  const [expandedApprovers, setExpandedApprovers] = useState(new Set());
  const [approverContracts, setApproverContracts] = useState({});
  const [approverInviting, setApproverInviting] = useState(null);
  const [approverMsg, setApproverMsg] = useState({});

  const loadApprovers = () => {
    setApproversLoading(true);
    api.get('resource-approvers').then(r => { setApprovers(r || []); setApproversLoading(false); }).catch(() => setApproversLoading(false));
  };

  const toggleApproverExpand = async (id) => {
    setExpandedApprovers(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      next.add(id);
      return next;
    });
    if (!approverContracts[id]) {
      const rows = await api.get(`resource-approvers/${id}/contracts`).catch(() => []);
      setApproverContracts(prev => ({ ...prev, [id]: rows || [] }));
    }
  };

  const sendPortalInvite = async (id) => {
    setApproverInviting(id);
    setApproverMsg(prev => ({ ...prev, [id]: '' }));
    try {
      const res = await api.post(`resource-approvers/${id}/portal-invite`, {});
      setApproverMsg(prev => ({ ...prev, [id]: `Invite sent. Temp password: ${res.temp_password}` }));
      loadApprovers();
    } catch (e) {
      setApproverMsg(prev => ({ ...prev, [id]: e.message || 'Failed to send invite' }));
    } finally {
      setApproverInviting(null);
    }
  };

  const toggleApproverPortal = async (id, currentVal) => {
    await api.put(`resource-approvers/${id}`, { is_portal_active: currentVal ? 0 : 1 });
    loadApprovers();
  };

  const unlockApprover = async (id) => {
    await api.post(`resource-approvers/${id}/unlock`, {});
    setApproverMsg(prev => ({ ...prev, [id]: 'Account unlocked.' }));
    loadApprovers();
  };

  useEffect(() => { loadApprovers(); }, []);

  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <span className="text-[11px] font-bold text-navy">Timesheet Approvers</span>
          <span className="ml-2 text-[9px] text-gray-400">Client contacts configured to approve contractor timesheets · portal at /client-portal</span>
        </div>
        <button onClick={loadApprovers} className="text-[9px] px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">↻ Refresh</button>
      </div>
      {approversLoading && <div className="text-center py-10 text-gray-400 text-xs">Loading…</div>}
      {!approversLoading && approvers.length === 0 && (
        <div className="text-center py-10 text-gray-400 text-[10px]">
          No client contacts are configured as timesheet approvers yet.<br />
          <span className="text-gray-400">To set one up: edit a contractor's contract and set Timesheet Approver → Client Contact.</span>
        </div>
      )}
      {!approversLoading && approvers.length > 0 && (
        <div>
          {approvers.map(a => {
            const isExpanded = expandedApprovers.has(a.id);
            const contracts = approverContracts[a.id] || [];
            const msg = approverMsg[a.id] || '';
            return (
              <div key={a.id} className="border-b border-gray-100 last:border-b-0">
                <div className="px-4 py-3 flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-navy">
                    {(a.first_name?.[0] || '').toUpperCase()}{(a.last_name?.[0] || '').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-navy">{a.first_name} {a.last_name}</span>
                      {a.role && <span className="text-[8px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{a.role}</span>}
                      {a.locked_at && <span className="text-[7px] bg-red-600 text-white px-1.5 py-0.5 rounded font-bold">🔒 Blocked</span>}
                      {a.is_portal_active
                        ? <span className="text-[7px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">Portal Active</span>
                        : <span className="text-[7px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-semibold">No Portal Access</span>}
                      {a.pending_approvals > 0 && <span className="text-[7px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">{a.pending_approvals} pending</span>}
                    </div>
                    <div className="text-[9px] text-gray-500 mt-0.5">{a.email} · {a.client_name}</div>
                    <div className="text-[8px] text-gray-400 mt-0.5 flex gap-3 flex-wrap">
                      <span>{a.contract_count} contract{a.contract_count !== 1 ? 's' : ''}</span>
                      {a.is_portal_active && <span>Last login: {a.last_login ? new Date(a.last_login).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}</span>}
                    </div>
                    {msg && (
                      <div className={`mt-1.5 text-[9px] px-2 py-1 rounded ${msg.toLowerCase().includes('fail') || msg.toLowerCase().includes('error') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                        {msg}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    {a.locked_at && (
                      <button onClick={() => unlockApprover(a.id)} className="text-[8px] px-2 py-1 bg-amber-500 text-amber-950 rounded hover:bg-amber-600">Unlock</button>
                    )}
                    <button
                      onClick={() => sendPortalInvite(a.id)}
                      disabled={approverInviting === a.id}
                      className="text-[8px] px-2 py-1 bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-50">
                      {approverInviting === a.id ? 'Sending…' : a.is_portal_active ? '↻ Reset & Resend' : 'Send Invite'}
                    </button>
                    <button
                      onClick={() => toggleApproverPortal(a.id, a.is_portal_active)}
                      className={`text-[8px] px-2 py-1 rounded ${a.is_portal_active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                      {a.is_portal_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => toggleApproverExpand(a.id)}
                      className="text-[8px] px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
                      {isExpanded ? '▲ Hide' : '▼ Contracts'}
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-3 pl-14">
                    {contracts.length === 0 && <div className="text-[9px] text-gray-400 italic">Loading contracts…</div>}
                    {contracts.map(c => {
                      const CONTRACT_STATUS_CLS = { draft:'bg-gray-100 text-gray-500', issued:'bg-blue-100 text-blue-700', active:'bg-green-100 text-green-700', completed:'bg-purple-100 text-purple-600', cancelled:'bg-red-100 text-red-500' };
                      return (
                        <div key={c.id} className="flex items-center gap-3 py-1.5 border-t border-gray-50 first:border-t-0">
                          {c.reference && <span className="text-[8px] font-mono font-bold text-navy bg-navy/5 px-1.5 py-0.5 rounded flex-shrink-0">{c.reference}</span>}
                          <span className={`text-[7px] px-1.5 py-0.5 rounded font-semibold capitalize flex-shrink-0 ${CONTRACT_STATUS_CLS[c.status] || 'bg-gray-100 text-gray-500'}`}>{c.status}</span>
                          <span className="text-[9px] text-gray-600 flex-1 min-w-0 truncate">
                            {onOpenContractor && c.resource_worker_id
                              ? <button onClick={() => onOpenContractor(c.resource_worker_id)} className="font-semibold text-navy hover:underline">{c.worker_name}</button>
                              : c.worker_name}
                            {' '}{c.sow_reference ? `· ${c.sow_reference}` : ''}{c.sow_detail ? ` · ${c.sow_detail.slice(0,40)}` : ''}
                          </span>
                          {c.pending_approvals > 0 && (
                            <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                              <span className="text-[7px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">{c.pending_approvals} pending</span>
                              {(c.pending_weeks || []).map(w => (
                                <span key={w} className="text-[7px] text-blue-500 font-mono">wk {w}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
